import { Link } from "react-router-dom";
import { resolvePlanLimits } from "@/utils/plans";
import type { Plan } from "@/types/plans";

interface PlanCardProps {
  plan:       Plan;
  onUpgrade?: () => void;
}

export default function PlanCard({ plan, onUpgrade }: PlanCardProps) {
  // Only the actual Free plan is free — a boost plan may have its price set
  // later from the admin panel, so it must not display as "Free".
  const isFree = plan.slug === "free";
  const hasPrice = plan.priceYearly > 0;

  // Read the same allowances the business form uses, straight from the plan
  // copy — so the card can never advertise numbers the form will not honour.
  const limits = resolvePlanLimits({
    slug: plan.slug,
    name: plan.name,
    tagline: plan.tagline,
    highlights: plan.highlights,
  });

  const allowanceChips = [
    {
      icon: "ri-layout-grid-line",
      label: `${limits.categories} ${limits.categories === 1 ? "category" : "categories"}`,
      accent: false,
    },
    ...(limits.subcategories > 0
      ? [{
          icon: "ri-node-tree",
          label: `${limits.subcategories} sub ${limits.subcategories === 1 ? "category" : "categories"}`,
          accent: false,
        }]
      : []),
    {
      icon: "ri-image-line",
      label: `${limits.photos} ${limits.photos === 1 ? "photo" : "photos"}`,
      accent: false,
    },
    ...(limits.video
      ? [{ icon: "ri-video-line", label: "Video tour", accent: true }]
      : []),
  ];

  return (
    <article
      id={`plan-${plan.slug}`}
      className={`relative flex h-full scroll-mt-24 flex-col rounded-lg border bg-background-50 p-6 transition-shadow md:p-7 ${
        plan.popular ? "border-primary-400 ring-1 ring-primary-200" : "border-background-200"
      }`}
    >
      {plan.popular ? (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-foreground-950">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-star-fill text-[12px]" />
          </span>
          Most popular
        </span>
      ) : null}

      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
        <i className={`${plan.icon} text-xl`} />
      </span>

      <h3 className="mt-4 font-heading text-xl font-extrabold text-foreground-950">{plan.name}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{plan.tagline}</p>

      <div className="mt-5 flex items-end gap-1.5">
        {hasPrice ? (
          <>
            <span className="font-heading text-3xl font-extrabold text-foreground-950">
              ₹{plan.priceYearly.toLocaleString("en-IN")}
            </span>
            <span className="pb-1 text-sm text-foreground-500">/year</span>
          </>
        ) : isFree ? (
          <span className="font-heading text-3xl font-extrabold text-foreground-950">Free</span>
        ) : (
          <span className="font-heading text-3xl font-extrabold text-foreground-950">Contact us</span>
        )}
      </div>
      <p className="mt-1 text-xs text-foreground-500">
        {`Listing validity: ${plan.duration} `}
        <Link
          to="/terms-and-conditions"
          className="cursor-pointer font-medium text-secondary-700 underline decoration-dotted underline-offset-2 transition-colors hover:text-primary-700"
        >
          (Conditions Apply)
        </Link>
      </p>

      <div className="mt-5 rounded-md border border-background-200 bg-background-100/70 p-3">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-foreground-500">
          What you can add
        </p>
        <div className="flex flex-wrap gap-1.5">
          {allowanceChips.map((chip) => (
            <span
              key={chip.label}
              className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-foreground-700"
            >
              <span
                className={`flex h-3.5 w-3.5 items-center justify-center ${
                  chip.accent ? "text-accent-600" : "text-secondary-600"
                }`}
              >
                <i className={`${chip.icon} text-[13px]`} />
              </span>
              {chip.label}
            </span>
          ))}
        </div>
      </div>

      <ul className="mt-5 flex flex-1 flex-col gap-2.5 border-t border-background-200 pt-5">
        {plan.highlights.map((highlight) => (
          <li key={highlight} className="flex items-start gap-2.5 text-sm text-foreground-700">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
              <i className="ri-check-line text-base" />
            </span>
            {highlight}
          </li>
        ))}
      </ul>

      {/* CTA — modal when onUpgrade provided, link otherwise */}
      {onUpgrade ? (
        <button
          type="button"
          onClick={onUpgrade}
          className={`mt-6 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-semibold transition-colors duration-200 ${
            plan.popular
              ? "bg-primary-500 text-background-50 hover:bg-primary-600"
              : "border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-700"
          }`}
        >
          {isFree ? "Get Started Free" : `Upgrade to ${plan.name}`}
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-line text-base" />
          </span>
        </button>
      ) : (
        <Link
          to={`/list-your-business?plan=${plan.slug}`}
          className={`mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-semibold transition-colors duration-200 ${
            plan.popular
              ? "bg-primary-500 text-background-50 hover:bg-primary-600"
              : "border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-700"
          }`}
        >
          {plan.ctaLabel}
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-line text-base" />
          </span>
        </Link>
      )}
    </article>
  );
}